/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import TableHeader from 'components/Table/TableHeader';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initParent = {
  id: '',
  code: '',
  remark: '',
  shift_code: '',
  cashier_name: '',
  branch_name: '',
  money_send: '',
  status: 'edit',
  created_at: '',
  updated_at: '',
  total: '',
  date_record: Func.dateNow(),
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

function CloseShiftReturn() {
  const mod = 'close_shift_return';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(initParent);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40, sort: false },
    { id: 'tb.status', label: 'สถานะ', align: 'center', sort: false },
    { id: 'tb.code', label: t(`attr.code`), align: 'center', sort: false },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left', sort: false },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left', sort: false },
    { id: 'cs.code', label: 'รอบ', align: 'center', sort: false },
    { id: 'ch.name', label: 'เครื่อง POS', align: 'left', sort: false },
    { id: 'tb.nettotal', label: 'จำนวนเงิน', align: 'right', sort: false },
    { id: 'tb.created_at', label: 'วันที่', align: 'center', sort: false },
  ];

  const view = () => {
    setLoading(true);
    setRecord([]);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/return_product/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableHeader data={initHeader} />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell
                        align='center'
                        onClick={() => {
                          setPhoto(row);
                          setModalPhoto(true);
                        }}
                      >
                        {Func.imageDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{Func.statusFaDisplay(row)}</TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='left'>{row.branch_code}</TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.shift_code}
                      </TableCell>
                      <TableCell align='left'>{row.cashier_name}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.nettotal, 2)}</TableCell>
                      <TableCell align='center' className='nowarp'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={7}>
                      <b>คืนสำเร็จ</b>
                    </TableCell>
                    <TableCell className='nowrap' align='right'>
                      <b>{Func.numberDisplay(parent.return_total, 2)}</b>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.name}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderPhoto()}
      {renderLoading()}
    </div>
  );
}

export default CloseShiftReturn;
